import type {
  FundamentalsBackingManifest,
  StemManifest,
  Tempo,
} from "../types";
import {
  AcousticDrumSampler,
  type AcousticDrumVoice,
} from "./acoustic-drum-sampler";
import { createMasterInput } from "./audio-master-bus";

export type BackingInstrument = "guitar" | "bass";

export class MetalTeachingKit {
  private context?: AudioContext;
  private sampler?: AcousticDrumSampler;
  private kitManifestUrl = "";
  private backingManifestUrl = "";
  private backingManifest?: FundamentalsBackingManifest;
  private backingBase = "";
  private backingBuffers = new Map<string, AudioBuffer>();
  private backingGains = new Map<BackingInstrument, GainNode>();
  private backingSources = new Set<AudioBufferSourceNode>();
  private backingEnabled: Record<BackingInstrument, boolean> = {
    guitar: true,
    bass: true,
  };
  private backingLevels: Record<BackingInstrument, number> = {
    guitar: -1.5,
    bass: 0.5,
  };
  private drumVoiceLevels = new Map<AcousticDrumVoice, number>();
  private loadPromise?: Promise<void>;

  async ready(
    kitManifestUrl: string,
    backingManifestUrl: string,
  ): Promise<void> {
    if (!kitManifestUrl) throw new Error("The metal teaching kit is unavailable");
    if (!backingManifestUrl) throw new Error("The play-along riffs are unavailable");
    if (
      this.loadPromise
      && kitManifestUrl === this.kitManifestUrl
      && backingManifestUrl === this.backingManifestUrl
    ) return this.loadPromise;
    this.destroy();
    this.kitManifestUrl = kitManifestUrl;
    this.backingManifestUrl = backingManifestUrl;
    this.loadPromise = this.load(kitManifestUrl, backingManifestUrl).catch((error: unknown) => {
      this.loadPromise = undefined;
      throw error;
    });
    return this.loadPromise;
  }

  private async load(
    kitManifestUrl: string,
    backingManifestUrl: string,
  ): Promise<void> {
    const [kitResponse, backingResponse] = await Promise.all([
      fetch(kitManifestUrl),
      fetch(backingManifestUrl),
    ]);
    if (!kitResponse.ok) {
      throw new Error(`Metal kit manifest returned ${kitResponse.status}`);
    }
    if (!backingResponse.ok) {
      throw new Error(`Play-along manifest returned ${backingResponse.status}`);
    }
    const [manifest, backingManifest] = await Promise.all([
      kitResponse.json() as Promise<StemManifest>,
      backingResponse.json() as Promise<FundamentalsBackingManifest>,
    ]);
    if (!manifest.sandboxKit) throw new Error("Metal kit samples are missing");
    if (backingManifest.bars !== 4 || backingManifest.bpm !== 110) {
      throw new Error("The play-along riffs do not match the four-bar lesson");
    }
    this.backingManifest = backingManifest;
    this.backingBase = backingManifestUrl.slice(
      0,
      backingManifestUrl.lastIndexOf("/") + 1,
    );
    this.context = new AudioContext({ sampleRate: manifest.sampleRate });
    const master = createMasterInput(this.context, manifest.masterBus);
    for (const instrument of ["guitar", "bass"] as const) {
      const gain = this.context.createGain();
      gain.gain.value = this.backingGain(instrument);
      gain.connect(master);
      this.backingGains.set(instrument, gain);
    }
    const base = kitManifestUrl.slice(0, kitManifestUrl.lastIndexOf("/") + 1);
    // The source bank was calibrated to sit above a full guitar-and-bass bed.
    // A small trim preserves that punch without making a drums-only lesson harsh.
    this.sampler = new AcousticDrumSampler(
      this.context,
      base,
      manifest.sandboxKit,
      master,
      -4,
    );
    await this.sampler.load();
    this.sampler.setEnabled(true);
    for (const [voice, levelDb] of this.drumVoiceLevels) {
      this.sampler.setVoiceLevelDb(voice, levelDb);
    }
    await this.context.resume();
  }

  private backingGain(instrument: BackingInstrument): number {
    return this.backingEnabled[instrument]
      ? 10 ** (this.backingLevels[instrument] / 20)
      : 0;
  }

  private setBackingGain(instrument: BackingInstrument): void {
    if (!this.context) return;
    const gain = this.backingGains.get(instrument);
    if (!gain) return;
    gain.gain.cancelScheduledValues(this.context.currentTime);
    gain.gain.setTargetAtTime(
      this.backingGain(instrument),
      this.context.currentTime,
      0.012,
    );
  }

  setBackingEnabled(instrument: BackingInstrument, enabled: boolean): void {
    this.backingEnabled[instrument] = enabled;
    this.setBackingGain(instrument);
  }

  setBackingLevelDb(instrument: BackingInstrument, levelDb: number): void {
    this.backingLevels[instrument] = Math.max(-12, Math.min(6, levelDb));
    this.setBackingGain(instrument);
  }

  setDrumVoiceLevelDb(voice: AcousticDrumVoice, levelDb: number): void {
    const bounded = Math.max(-12, Math.min(6, levelDb));
    this.drumVoiceLevels.set(voice, bounded);
    this.sampler?.setVoiceLevelDb(voice, bounded);
  }

  private async backingBuffer(
    riff: string,
    tempo: Tempo,
    instrument: BackingInstrument,
  ): Promise<AudioBuffer> {
    if (!this.context || !this.backingManifest) {
      throw new Error("The play-along engine is not ready");
    }
    const key = `${riff}:${tempo}:${instrument}`;
    const cached = this.backingBuffers.get(key);
    if (cached) return cached;
    const definition = this.backingManifest.tempos[tempo]?.stems[riff];
    if (!definition) throw new Error(`The ${riff} riff is unavailable at ${tempo}×`);
    const response = await fetch(this.backingBase + definition[instrument]);
    if (!response.ok) {
      throw new Error(`${definition[instrument]} returned ${response.status}`);
    }
    const decoded = await this.context.decodeAudioData(await response.arrayBuffer());
    this.backingBuffers.set(key, decoded);
    return decoded;
  }

  async startBacking(riff: string, tempo: Tempo): Promise<number> {
    if (!this.context || !this.backingManifest) {
      throw new Error("The play-along engine is not ready");
    }
    this.stopBacking();
    if (!riff) return 0;
    const [guitar, bass] = await Promise.all([
      this.backingBuffer(riff, tempo, "guitar"),
      this.backingBuffer(riff, tempo, "bass"),
    ]);
    const tempoDefinition = this.backingManifest.tempos[tempo];
    const startAt = this.context.currentTime + 0.08;
    for (const [instrument, buffer] of [
      ["guitar", guitar],
      ["bass", bass],
    ] as const) {
      const source = this.context.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      source.loopEnd = tempoDefinition.durationSeconds;
      source.connect(this.backingGains.get(instrument)!);
      this.backingSources.add(source);
      source.onended = () => {
        this.backingSources.delete(source);
        source.disconnect();
      };
      source.start(startAt);
    }
    return Math.max(0, (startAt - this.context.currentTime) * 1_000);
  }

  hit(voice: AcousticDrumVoice, velocity: number): void {
    this.sampler?.hit(voice, velocity);
  }

  private stopBacking(): void {
    for (const source of this.backingSources) {
      try { source.stop(); } catch { /* already stopped */ }
      source.disconnect();
    }
    this.backingSources.clear();
  }

  stop(): void {
    this.sampler?.stop();
    this.stopBacking();
  }

  destroy(): void {
    this.stopBacking();
    this.sampler?.destroy();
    this.sampler = undefined;
    for (const gain of this.backingGains.values()) gain.disconnect();
    this.backingGains.clear();
    this.backingBuffers.clear();
    this.backingManifest = undefined;
    this.backingBase = "";
    void this.context?.close();
    this.context = undefined;
    this.loadPromise = undefined;
    this.kitManifestUrl = "";
    this.backingManifestUrl = "";
  }
}
